import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listDriverApplications,
  getDriverApplication,
  fetchDocumentBlobUrl,
  approveDriverApplication,
  rejectDriverApplication,
} from './adminApi';

const STATUS_FILTERS = ['pending', 'resubmission_requested', 'approved', 'rejected', 'all'];

function DocumentThumb({ applicationId, docKey, secret }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let revoked = null;
    fetchDocumentBlobUrl(applicationId, docKey, secret)
      .then((url) => { revoked = url; setBlobUrl(url); })
      .catch((err) => setError(err.message));
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [applicationId, docKey, secret]);

  return (
    <div className="adm-doc-card">
      <div className="adm-doc-label">{docKey.replace(/_/g, ' ')}</div>
      {error ? (
        <div className="adm-doc-error">{error}</div>
      ) : blobUrl ? (
        <a href={blobUrl} target="_blank" rel="noreferrer">
          <img src={blobUrl} alt={docKey} className="adm-doc-img" />
        </a>
      ) : (
        <div className="adm-doc-loading">Loading…</div>
      )}
    </div>
  );
}

function DriverDetail({ application, secret, onBack, onDecided }) {
  const [reviewerName, setReviewerName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleApprove = async () => {
    if (!reviewerName.trim()) { setError('Enter your name as the reviewer first.'); return; }
    setBusy(true); setError(null);
    try {
      await approveDriverApplication(application.id, reviewerName.trim(), notes.trim(), secret);
      onDecided();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const handleReject = async () => {
    if (!reviewerName.trim()) { setError('Enter your name as the reviewer first.'); return; }
    if (!notes.trim()) { setError('A rejection reason is required.'); return; }
    setBusy(true); setError(null);
    try {
      await rejectDriverApplication(application.id, reviewerName.trim(), notes.trim(), secret);
      onDecided();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const documentEntries = Object.keys(application.documents || {});
  const isDecided = application.status === 'approved' || application.status === 'rejected';

  return (
    <div>
      <button className="adm-back-btn" onClick={onBack}>&larr; Back to queue</button>
      <div className="adm-detail-header">
        <h2 className="adm-detail-name">{application.username}</h2>
        <span className={`adm-status-pill adm-status-${application.status}`}>{application.status.replace('_', ' ')}</span>
      </div>
      <div className="adm-detail-meta">{application.email} &middot; {application.country} &middot; applied {new Date(application.createdAt).toLocaleString()}</div>

      <div className="adm-detail-grid">
        <div className="adm-detail-card">
          <h4>Applicant</h4>
          <div className="adm-detail-row"><span>Phone</span><span>{application.phoneNumber || '—'}</span></div>
          <div className="adm-detail-row"><span>Type</span><span>{application.existingUserId ? `Renewal (driver #${application.existingUserId})` : 'New applicant'}</span></div>
        </div>
        <div className="adm-detail-card">
          <h4>Vehicle</h4>
          <div className="adm-detail-row"><span>Type</span><span>{application.vehicleType || '—'}</span></div>
          <div className="adm-detail-row"><span>Make / Model</span><span>{[application.vehicleMake, application.vehicleModel].filter(Boolean).join(' ') || '—'}</span></div>
          <div className="adm-detail-row"><span>Year</span><span>{application.vehicleYear || '—'}</span></div>
          <div className="adm-detail-row"><span>Plate</span><span>{application.licensePlate || '—'}</span></div>
        </div>
        <div className="adm-detail-card">
          <h4>Expiry Dates</h4>
          <div className="adm-detail-row"><span>License</span><span>{application.licenseExpiresAt ? new Date(application.licenseExpiresAt).toLocaleDateString() : '—'}</span></div>
          <div className="adm-detail-row"><span>Insurance</span><span>{application.insuranceExpiresAt ? new Date(application.insuranceExpiresAt).toLocaleDateString() : '—'}</span></div>
          <div className="adm-detail-row"><span>Registration</span><span>{application.registrationExpiresAt ? new Date(application.registrationExpiresAt).toLocaleDateString() : '—'}</span></div>
        </div>
      </div>

      <h4 className="adm-section-heading">Documents</h4>
      <p className="adm-section-note">Real human review only - no automated background check runs here. Compare the selfie against the ID photo yourself before approving.</p>
      <div className="adm-doc-grid">
        {documentEntries.length === 0 ? (
          <p className="adm-section-note">No documents on file.</p>
        ) : (
          documentEntries.map((key) => <DocumentThumb key={key} applicationId={application.id} docKey={key} secret={secret} />)
        )}
      </div>

      {application.reviewedBy && (
        <div className={`adm-decided-note adm-decided-note--${application.status === 'approved' ? 'approved' : 'rejected'}`}>
          {application.status === 'approved' ? 'Approved' : 'Rejected'} by <strong>{application.reviewedBy}</strong> on {new Date(application.reviewedAt).toLocaleString()}
          {(application.reviewNotes || application.rejectionReason) && <> — "{application.reviewNotes || application.rejectionReason}"</>}
        </div>
      )}

      {!isDecided && (
        <div className="adm-review-box">
          <h4>Reviewer Decision</h4>
          <input className="adm-review-input" placeholder="Your name (reviewer)" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} />
          <textarea className="adm-review-input" style={{ height: 70, resize: 'vertical' }} placeholder="Notes (required if rejecting)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          {error && <div className="adm-error-banner">{error}</div>}
          <div className="adm-review-actions">
            <button className="adm-approve-btn" onClick={handleApprove} disabled={busy}>{busy ? 'Working…' : 'Approve'}</button>
            <button className="adm-reject-btn" onClick={handleReject} disabled={busy}>{busy ? 'Working…' : 'Reject'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DriverApplicationsPanel({ secret }) {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [applications, setApplications] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);

  const loadQueue = useCallback(() => {
    setLoading(true); setError(null);
    listDriverApplications(statusFilter, secret)
      .then(setApplications)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [statusFilter, secret]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    if (!selectedId) { setSelectedApplication(null); return; }
    getDriverApplication(selectedId, secret).then(setSelectedApplication).catch((err) => setError(err.message));
  }, [selectedId, secret]);

  const handleDecided = () => { setSelectedId(null); setSelectedApplication(null); loadQueue(); };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return applications;
    return applications.filter((a) =>
      a.username?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q) || a.country?.toLowerCase().includes(q)
    );
  }, [applications, search]);

  if (selectedApplication) {
    return <DriverDetail application={selectedApplication} secret={secret} onBack={() => setSelectedId(null)} onDecided={handleDecided} />;
  }

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Driver Applications</h1>
          <p className="adm-page-subtitle">
            Real human review only - no automated background-check vendor is wired up here. Approving creates the
            driver's real account (new applicants) or refreshes their expiry dates (renewals); rejecting requires a reason.
          </p>
        </div>
        <button className="adm-refresh-btn" onClick={loadQueue} disabled={loading}>{loading ? 'Loading…' : '⟳ Refresh'}</button>
      </div>

      <div className="adm-toolbar">
        <div className="adm-filter-pills">
          {STATUS_FILTERS.map((s) => (
            <button key={s} className={`adm-filter-pill${s === statusFilter ? ' adm-filter-pill-active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>
        <input className="adm-search-input" placeholder="Search name, email, country…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {error && <div className="adm-error-banner">{error}</div>}

      {filtered.length === 0 ? (
        <div className="adm-empty-state">No applications match this view.</div>
      ) : (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Country</th><th>Vehicle</th><th>Type</th><th>Status</th><th>Submitted</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((app) => (
                <tr key={app.id}>
                  <td>{app.username}</td>
                  <td>{app.email}</td>
                  <td>{app.country}</td>
                  <td>{app.vehicleType || '—'}</td>
                  <td>{app.existingUserId ? 'Renewal' : 'New'}</td>
                  <td><span className={`adm-status-pill adm-status-${app.status}`}>{app.status.replace('_', ' ')}</span></td>
                  <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                  <td><button className="adm-review-btn" onClick={() => setSelectedId(app.id)}>Review</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
